// ZapisatDoRozpisu — JEDEN zdieľaný dialóg „Zapísať do Rozpisu prác" (Mirko GO 2026-07-28).
//
// PREČO: ústava dovoľuje, aby v Rozpise bolo aj niečo, čo ešte NIE JE zákazka (budúca zákazka).
// Mirko: „daj možnosť aspoň urobiť zápis do rozpisu aj z dopytu aj z CP, ale hlavne PRENIESŤ
// ZÁKAZNÍK ÚDAJE, aby sme s ním ďalej mohli komunikovať."
//
// Preto sa spolu so zápisom prenášajú kontaktné údaje (work_schedule.kontakt_*) a väzba na zdroj
// (dopyt_id / quote_id) — v Rozpise potom vidno, komu volať, aj keď zákazka ešte neexistuje.
//
// Používajú ho: DopytyView (detail dopytu) a CenoveView (⋮ pri CP). Žiadne vlastné kópie — ústava 103.E/103.F.
import { KontaktnaOsoba } from './KontaktyPartnera.jsx'   // p97 — ústava 131.10.A, jeden blok kontaktnej osoby
import React, { useState, useEffect, useMemo } from 'react'
import TelefonInput from './TelefonInput.jsx'
import { fetchAuth } from '../auth/fetchAuth.js'
import { useToast } from './Toast.jsx'
import { COLORS, TYPO, SPACE, RADIUS, TINT, todayLocal } from '../theme.js'
import { SearchableSelect } from './Ui.jsx'
import { Field, FieldGrid, inp } from './DocFields.jsx'
import { Btn, BtnAdd } from './Btn.jsx'

const DNES = () => todayLocal()
const PLUS = (d, n) => { const x = new Date(d + 'T00:00:00'); x.setDate(x.getDate() + n); return x.toISOString().slice(0, 10) }

// Normalizacia nazvu pre fuzzy zhodu (bez diakritiky, medzier navyse, case)
const NORM = s => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ').trim()

export default function ZapisatDoRozpisu({ open, onClose, zdroj, onSaved }) {
  const toast = useToast()
  const [mode, setMode] = useState('new')   // 'new' = novy zapis · 'existing' = sparovat s uz existujucim (Mirko 3.8.)
  const [employees, setEmployees] = useState([])
  const [projects, setProjects] = useState([])
  const [wsEntries, setWsEntries] = useState([])
  const [existingWsId, setExistingWsId] = useState('')
  const [autoParovane, setAutoParovane] = useState(false)
  const [busy, setBusy] = useState(false)
  const [f, setF] = useState(null)

  useEffect(() => {
    if (!open) return
    setMode('new'); setExistingWsId('')
    fetchAuth('/api/employees').then(r => r.ok ? r.json() : []).then(d => setEmployees(Array.isArray(d) ? d : [])).catch(() => {})
    fetchAuth('/api/projects').then(r => r.ok ? r.json() : []).then(d => setProjects(Array.isArray(d) ? d : [])).catch(() => {})
    fetchAuth('/api/work-schedule').then(r => r.ok ? r.json() : []).then(d => setWsEntries(Array.isArray(d) ? d : [])).catch(() => {})
  }, [open])

  useEffect(() => {
    if (!open || !zdroj) return
    // Matrix má vedieť, či zákazka už existuje — skús ju nájsť podľa zákazníka + podobného názvu,
    // aj keď zdroj.project_id (napr. quotes.projectId) nikdy nebolo nastavené (Mirko 3.8.).
    let candidateId = zdroj.project_id || ''
    let auto = false
    if (!candidateId && zdroj.customerId && projects.length) {
      const nazovNorm = NORM(zdroj.nazov)
      const zhody = projects.filter(p => p.customerId === zdroj.customerId)
      const presna = zhody.find(p => NORM(p.name) === nazovNorm)
      const kand = presna || (zhody.length === 1 ? zhody[0] : null)
      if (kand) { candidateId = kand.id; auto = true }
    }
    setAutoParovane(auto)
    setF({
      employee_id: '',
      project_id: candidateId,
      project_name: candidateId ? '' : (zdroj.nazov || ''),
      start_date: DNES(),
      end_date: PLUS(DNES(), 4),
      status: 'planned',
      notes: '',
      kontakt_meno: zdroj.kontakt_meno || '',
      kontakt_telefon: zdroj.kontakt_telefon || '',
      kontakt_email: zdroj.kontakt_email || '',
      kontakt_adresa: zdroj.kontakt_adresa || '',
    })
  }, [open, zdroj, projects])

  const predaci = useMemo(() => [...employees]
    .filter(e => (e.is_partak || e.role === 'predák') && e.active !== 0 && e.active !== '0')
    .sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`, 'sk'))
    .map(e => ({ value: e.id, label: `${e.firstName} ${e.lastName}` })), [employees])

  const projectOptions = useMemo(() => [...projects]
    .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'sk'))
    .map(p => ({ value: p.id, label: p.name })), [projects])

  const empName = id => { const e = employees.find(x => x.id === id); return e ? `${e.firstName} ${e.lastName}` : (id || '') }
  const wsOptions = useMemo(() => [...wsEntries]
    .sort((a, b) => (b.start_date || '').localeCompare(a.start_date || ''))
    .map(w => ({
      value: w.id,
      label: `${w.project_name || w.projectName || '(bez zákazky)'} — ${empName(w.employee_id)} — ${w.start_date} → ${w.end_date}`
        + (w.quote_id ? ' [už spárované s CP]' : ''),
    })), [wsEntries, employees])

  function setProject(v) {
    const exists = projects.some(p => p.id === v)
    setAutoParovane(false)
    if (v && !exists) setF(s => ({ ...s, project_id: '', project_name: v }))
    else setF(s => ({ ...s, project_id: v, project_name: '' }))
  }

  if (!open || !f) return null
  const set = (k, v) => setF(s => ({ ...s, [k]: v }))
  const jeCp = zdroj.typ === 'cp'

  const uloz = async () => {
    if (mode === 'existing') {
      if (!existingWsId) { toast.error('Vyber existujúci zápis v Rozpise, s ktorým chceš CP spárovať.'); return }
      setBusy(true)
      try {
        // len domplní väzbu + kontakt (ak ho zápis este nema) — ostatné polia zápisu NEMENÍ (anti-data-loss)
        const body = {}
        if (jeCp) body.quote_id = String(zdroj.id || ''); else body.dopyt_id = String(zdroj.id || '')
        const cur = wsEntries.find(w => w.id === existingWsId) || {}
        if (f.kontakt_meno && !cur.kontakt_meno) body.kontakt_meno = f.kontakt_meno
        if (f.kontakt_telefon && !cur.kontakt_telefon) body.kontakt_telefon = f.kontakt_telefon
        if (f.kontakt_email && !cur.kontakt_email) body.kontakt_email = f.kontakt_email
        if (f.kontakt_adresa && !cur.kontakt_adresa) body.kontakt_adresa = f.kontakt_adresa
        const r = await fetchAuth('/api/work-schedule/' + existingWsId, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
        if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || 'HTTP ' + r.status) }
        toast.success('Spárované s existujúcim zápisom v Rozpise.')
        onSaved && onSaved()
        onClose && onClose()
      } catch (e) { toast.error('Nepodarilo sa: ' + e.message) }
      finally { setBusy(false) }
      return
    }
    if (!f.employee_id) { toast.error('Vyber predáka / partiu — bez toho zápis do Rozpisu nevznikne.'); return }
    if (!f.project_id && !String(f.project_name || '').trim()) { toast.error('Zadaj názov — buď vyber zákazku, alebo napíš názov budúcej.'); return }
    if (f.end_date < f.start_date) { toast.error('Dátum „Do" nemôže byť skôr ako „Od".'); return }
    setBusy(true)
    try {
      const body = {
        employee_id: f.employee_id,
        project_id: f.project_id || '',
        project_name: f.project_id ? '' : String(f.project_name || '').trim(),
        start_date: f.start_date, end_date: f.end_date,
        status: f.status, notes: f.notes || '',
        kontakt_meno: f.kontakt_meno || '', kontakt_telefon: f.kontakt_telefon || '',
        kontakt_email: f.kontakt_email || '', kontakt_adresa: f.kontakt_adresa || '',
        dopyt_id: jeCp ? '' : String(zdroj.id || ''),
        quote_id: jeCp ? String(zdroj.id || '') : '',
      }
      const r = await fetchAuth('/api/work-schedule', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || 'HTTP ' + r.status) }
      toast.success('Zapísané do Rozpisu prác' + (f.project_id ? '' : ' ako budúca zákazka') + '.')
      onSaved && onSaved()
      onClose && onClose()
    } catch (e) { toast.error('Nepodarilo sa: ' + e.message) }
    finally { setBusy(false) }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: SPACE.md }}
      onMouseDown={e => { if (e.target === e.currentTarget) onClose && onClose() }}>
      <div style={{ background: '#fff', borderRadius: RADIUS.lg, width: 'min(620px, 96vw)', maxHeight: '92vh', overflow: 'auto', boxShadow: '0 18px 48px rgba(15,23,42,.3)' }}>
        <div style={{ padding: `${SPACE.md}px ${SPACE.lg}px`, borderBottom: `1px solid ${COLORS.line}` }}>
          <div style={{ fontWeight: TYPO.weights.extra, fontSize: TYPO.sizes.lg }}>Zapísať do Rozpisu prác</div>
          <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginTop: 2 }}>
            {jeCp ? 'Z cenovej ponuky ' : 'Z dopytu '}<strong>{zdroj.popis || zdroj.id}</strong> — kontakt sa prenesie so zápisom
          </div>
        </div>

        <div style={{ padding: SPACE.lg, display: 'flex', flexDirection: 'column', gap: SPACE.md }}>
          <div style={{ display: 'flex', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md, overflow: 'hidden' }}>
            <div onClick={() => setMode('new')} style={{ flex: 1, textAlign: 'center', padding: '8px 0', fontSize: TYPO.sizes.sm, cursor: 'pointer', background: mode === 'new' ? COLORS.nav : '#fff', color: mode === 'new' ? '#fff' : COLORS.text, fontWeight: TYPO.weights.semibold }}>
              Nový zápis
            </div>
            <div onClick={() => setMode('existing')} style={{ flex: 1, textAlign: 'center', padding: '8px 0', fontSize: TYPO.sizes.sm, cursor: 'pointer', background: mode === 'existing' ? COLORS.nav : '#fff', color: mode === 'existing' ? '#fff' : COLORS.text, fontWeight: TYPO.weights.semibold }}>
              Spárovať s existujúcim
            </div>
          </div>

          {mode === 'existing' && (
            <Field label="Existujúci zápis v Rozpise">
              <SearchableSelect value={existingWsId} onChange={setExistingWsId} options={wsOptions} placeholder="Hľadať podľa zákazky, predáka…" menuPortal />
            </Field>
          )}

          {mode === 'new' && autoParovane && f.project_id && (
            <div style={{ padding: SPACE.sm, background: 'rgba(34,197,94,0.08)', border: `1px solid ${COLORS.add}`, borderRadius: RADIUS.md, fontSize: TYPO.sizes.xs, color: COLORS.text }}>
              Automaticky spárované s existujúcou zákazkou <strong>{(projects.find(p => p.id === f.project_id) || {}).name}</strong> — over, prípadne zmeň nižšie v poli „Zákazka".
            </div>
          )}
          {mode === 'new' && !f.project_id && (
            <div style={{ padding: SPACE.sm, background: TINT.total || 'rgba(245,158,11,0.07)', border: `1px solid ${COLORS.total || '#fbbf24'}`, borderRadius: RADIUS.md, fontSize: TYPO.sizes.xs, color: COLORS.text }}>
              Žiadna zhodná zákazka sa nenašla — zápis bude v Rozpise <strong>oranžový (nespárovaný)</strong>, kým ju nevytvoríš alebo nespáruješ nižšie.
              Je to v poriadku: v Rozpise smie byť aj práca, ktorá zákazkou ešte nie je.
            </div>
          )}

          {mode === 'new' && (
            <FieldGrid>
              <Field label="Predák / partia *">
                <SearchableSelect value={f.employee_id} onChange={v => set('employee_id', v)} options={predaci} placeholder="Hľadať predáka…" />
              </Field>
              <Field label="Zákazka — vyhľadaj existujúcu, alebo napíš názov budúcej *">
                <SearchableSelect value={f.project_id || f.project_name} onChange={setProject} options={projectOptions}
                  placeholder="Hľadať zákazku, alebo napíš nový názov…" allowCreate menuPortal />
              </Field>
              <Field label="Od *"><input style={inp} type="date" value={f.start_date}
                onChange={e => setF(s => ({ ...s, start_date: e.target.value, end_date: s.end_date >= e.target.value ? s.end_date : e.target.value }))} /></Field>
              <Field label="Do *"><input style={inp} type="date" value={f.end_date} onChange={e => set('end_date', e.target.value)} /></Field>
            </FieldGrid>
          )}

          <div style={{ padding: SPACE.sm, background: TINT.nav, border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md }}>
            <div style={{ fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold, color: COLORS.nav, textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: SPACE.sm }}>
              Kontakt na zákazníka — prenáša sa do Rozpisu
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACE.sm }}>
              {/* p97 ústava 131.10.A: JEDEN blok kontaktnej osoby — osoba z registra partnera */}
              <KontaktnaOsoba customerId={zdroj && zdroj.customerId} inp={inp}
                osoba={{ meno: f.kontakt_meno, telefon: f.kontakt_telefon, email: f.kontakt_email }}
                onZmena={o => { set('kontakt_meno', o.meno); set('kontakt_telefon', o.telefon); set('kontakt_email', o.email) }}
                popisy={{ osoba: 'Zákazník / kontaktná osoba' }}
                obal={(tx, pole, kl) => <Field key={kl} label={tx}>{pole}</Field>} />
              <Field label="Adresa stavby"><input style={inp} value={f.kontakt_adresa} onChange={e => set('kontakt_adresa', e.target.value)} /></Field>
            </div>
          </div>

          {mode === 'new' && (
            <Field label="Poznámka k zápisu (NIE komunikácia)">
              <textarea style={{ ...inp, resize: 'vertical', minHeight: 38 }} value={f.notes} onChange={e => set('notes', e.target.value)}
                placeholder="napr. nastupujú v utorok, kľúče u suseda" />
            </Field>
          )}
          <div style={{ marginTop: -SPACE.sm, fontSize: TYPO.sizes.xs, color: COLORS.muted }}>
            Hovory, SMS a dohody so zákazníkom zapisuj až v Rozpise — dvojklikom na blok sa otvorí panel komunikácie.
          </div>
        </div>

        <div style={{ padding: `${SPACE.sm}px ${SPACE.lg}px`, borderTop: `1px solid ${COLORS.line}`, display: 'flex', justifyContent: 'flex-end', gap: SPACE.sm, background: COLORS.bgSoft }}>
          <Btn variant="ghost" onClick={onClose}>Zrušiť</Btn>
          <BtnAdd onClick={uloz} disabled={busy}>{busy ? 'Zapisujem…' : (mode === 'existing' ? 'Spárovať' : 'Zapísať do Rozpisu')}</BtnAdd>
        </div>
      </div>
    </div>
  )
}
